import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import getCoreServerSideProps from '../modules/core/getCoreServerSideProps';
import RendererWrapper from '../modules/helpers/RendererWrapper';
import { getFooterProps } from '../modules/landing-page/helpers/getLandingPageProps';
import {
  getEditModeMenu,
  getPageEditLinkProps,
} from '../modules/helpers/getPageProps';
import { getErrorPageApiContent } from '~/config/common/staticContent/getErrorPageApiContent';
import { getErrorSiteApiContent } from '~/config/common/staticContent/getErrorSiteApiContent';
import { getHeaderProps } from '~/modules/career-pages/helpers/getCareerPageProps';
import { removeUndefined } from '~/helpers/removeUndefined';
import { useIntl } from 'react-intl';
import renderElements from '~/helpers/renderer/renderer';
import { useGetRichTextValues } from '~/config/useGetRichtTextValues';
import { getClientConfig } from '~/config/getClientConfig';
const { theme: defaultTheme } = getClientConfig();

const LandingPage = dynamic(
  () => import('~/modules/landing-page/components/templates/LandingPage'),
  {
    ssr: false,
  },
);

/**
 * Server-side function to fetch and provide props for the landing page.
 *
 * @param {import('next').GetServerSidePropsContext} ctx - The context object containing request and response information.
 * @returns {Promise<{props: any}>} An object containing the props for the landing page.
 */
export const getServerSideProps = async (ctx) => {
  return {
    props: removeUndefined(getCoreServerSideProps(ctx)), //replace undefined with null to prevent serializing error
  };
};

function LandingNextPage(props) {
  const intl = useIntl();

  const { richTextValues } = useGetRichTextValues();

  const { publicRuntimeConfig } = getConfig();
  const share = {
    label: intl.formatMessage({
      id: 'landingpage.share',
    }),
    channels: defaultTheme.shareBtns,
  };

  const { context } = props;

  const pageContent =
    'isError' in context
      ? getErrorPageApiContent({
          intl,
          pathname: context.pathname || '',
          statusCode: context.statusCode,
          richTextValues,
        })
      : props.pageContent;

  const siteContent =
    'isError' in context
      ? getErrorSiteApiContent({ intl, richTextValues })
      : props.siteContent;

  const openGraph = {
    ...pageContent.openGraph,
    ...siteContent.openGraph,
  };

  return (
    <RendererWrapper
      enableBackToTop={true}
      externalServices={siteContent}
      openGraph={openGraph}
      meta={pageContent.meta}
      context={props.context}
      language={pageContent.language}
      style={'DEFAULT'}
      pageType="lp"
    >
      <LandingPage
        content={renderElements(pageContent.content, undefined, {
          cmsBaseUrl: publicRuntimeConfig.cmsBaseUrl,
          lang: props.context?.locale,
          landingPageCode: props.context?.landingPageCode,
          isEditMode: 'isEditMode' in props.context && props.context.isEditMode,
        })}
        header={getHeaderProps(
          pageContent,
          siteContent,
          !('isError' in context),
        )}
        footer={getFooterProps(siteContent, intl)}
        share={pageContent.withShare ? share : undefined}
        editLinks={getEditModeMenu(siteContent)}
        pageEditLink={getPageEditLinkProps(pageContent)}
      />
    </RendererWrapper>
  );
}

export default LandingNextPage;
